import { useMemo } from 'react';
import { Alert, message, Tag, Typography } from 'antd';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import './accounts.css';

const { Title } = Typography;

const ADAPTER = { list: (q) => accountsApi.listTaxRates(q) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load tax rates.';
  message.error({ content: msg, key: 'tax-list' });
}

const TAX_TYPE_COLOR = {
  gst:       'blue',
  exempt:    'default',
  nil_rated: 'gold',
  non_gst:   'default',
  rcm:       'purple'
};

function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return <span className="erp-grid__muted">—</span>;
  return `${n.toFixed(2)}%`;
}
function fmtDate(v) {
  return v ? String(v).slice(0, 10) : <span className="erp-grid__muted">—</span>;
}

const columns = [
  { title: 'Code', dataIndex: 'taxCode', key: 'taxCode',
    sortField: 'tax_code', sorter: true,
    width: 140, fixed: 'left', dataType: 'string',
    render: (v) => <code className="erp-grid__code">{v}</code> },
  { title: 'Name', dataIndex: 'taxName', key: 'taxName',
    sortField: 'tax_name', sorter: true,
    width: 220, ellipsis: true, dataType: 'string' },
  { title: 'Type', dataIndex: 'taxType', key: 'taxType',
    sortField: 'tax_type', sorter: true,
    width: 110, dataType: 'enum',
    groupable: true, groupField: 'tax_type',
    filters: [
      { text: 'GST',       value: 'gst' },
      { text: 'Exempt',    value: 'exempt' },
      { text: 'Nil-rated', value: 'nil_rated' },
      { text: 'Non-GST',   value: 'non_gst' },
      { text: 'RCM',       value: 'rcm' }
    ],
    filterField: 'tax_type',
    render: (v) => (
      <Tag color={TAX_TYPE_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>
        {(v || '').replace('_', ' ').toUpperCase()}
      </Tag>
    ) },
  { title: 'GST %',  dataIndex: 'gstRate',  key: 'gstRate',
    width: 80, align: 'right', dataType: 'number', render: fmtPct },
  { title: 'CGST %', dataIndex: 'cgstRate', key: 'cgstRate',
    width: 80, align: 'right', dataType: 'number', render: fmtPct },
  { title: 'SGST %', dataIndex: 'sgstRate', key: 'sgstRate',
    width: 80, align: 'right', dataType: 'number', render: fmtPct },
  { title: 'IGST %', dataIndex: 'igstRate', key: 'igstRate',
    width: 80, align: 'right', dataType: 'number', render: fmtPct },
  { title: 'HSN/SAC', dataIndex: 'hsnSac', key: 'hsnSac',
    sortField: 'hsn_sac', sorter: true, width: 120, dataType: 'string',
    render: (v) => v ? <code className="erp-grid__code">{v}</code>
                     : <span className="erp-grid__muted">—</span> },
  { title: 'Effective From', dataIndex: 'effectiveFrom', key: 'effectiveFrom',
    sortField: 'effective_from', sorter: true,
    width: 130, dataType: 'date', render: fmtDate },
  { title: 'Effective To', dataIndex: 'effectiveTo', key: 'effectiveTo',
    sortField: 'effective_to', width: 130, dataType: 'date', render: fmtDate },
  { title: 'RCM', dataIndex: 'isRcm', key: 'isRcm',
    width: 70, align: 'center', dataType: 'boolean',
    filters: [{ text: 'RCM', value: true }, { text: 'Forward', value: false }],
    filterField: 'is_rcm',
    render: (v) => v ? '✓' : <span className="erp-grid__muted">—</span> },
  { title: 'Active', dataIndex: 'isActive', key: 'isActive',
    width: 75, align: 'center', dataType: 'boolean',
    filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
    filterField: 'is_active',
    render: (v) => v === false
      ? <span className="erp-grid__muted">Inactive</span>
      : <span style={{ color: '#16a34a' }}>Active</span> }
];

export default function TaxRatesPage() {
  const { gridHandlers } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const mergedColumns = useMemo(() => columns, []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Tax Rates</Title>
      <Alert type="info" banner showIcon style={{ marginBottom: 10 }}
        message="GST rate master with effective-date versioning. 8 standard codes (0/5/12/18/28 + exempt / nil / non-GST) seeded per tenant. Rates may change — apply new effective-date rows rather than editing historical bands." />
      <ErpDataGrid
        title="Tax Rate Master"
        gridId="accounts-tax-rates-v1"
        columns={mergedColumns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        searchable
        searchPlaceholder="Search by code / name / HSN…"
        searchShortcut
        emptyTitle="No tax rates"
        emptyText="Standard GST bands should be seeded automatically — contact admin if missing."
        {...gridHandlers}
      />
      </div>
    </ErpMasterShell>
  );
}
